import * as signalR from "@microsoft/signalr";
import urlcat from "urlcat";

import { ResultsLevel } from "../config";
import { Disposition, JobFunction } from "../job";
import { timeSpanFromMilliseconds, timeSpanZero } from "../timespan";
import Execution, { Events, ExecutionState } from "./execution";

enum InstanceState {
	NotStarted,
	Initializing,
	GettingItemsToProcess,
	Processing,
	Finalizing,
	Successful,
	Failed,
	Cancelled,
	TimedOut,
	Error
}

enum JobMethod {
	InitializeAsync,
	GetItemsAsync,
	Count,
	GetEnumerator,
	EnumeratorMoveNext,
	EnumeratorCurrent,
	GetItemIdAsync,
	ProcessAsync,
	FinalizeAsync
}

enum ApiDisposition {
	NotComplete,
	Successful = 5,
	Failed,
	Cancelled
}

function getInstanceState(execution: Execution): InstanceState {
	switch (execution.state) {
		case ExecutionState.NotStarted:
			return InstanceState.NotStarted;
		case ExecutionState.Initializing:
			return InstanceState.Initializing;
		case ExecutionState.GettingItems:
			return InstanceState.GettingItemsToProcess;
		case ExecutionState.Processing:
			return InstanceState.Processing;
		case ExecutionState.Finalizing:
			return InstanceState.Finalizing;
		case ExecutionState.Complete:
			return execution.isCancelled
				? InstanceState.Cancelled
				: execution.isFailed
				? InstanceState.Failed
				: InstanceState.Successful;
		default:
			throw new Error("Unknown state");
	}
}

function getJobMethod(func: JobFunction): JobMethod {
	switch (func) {
		case JobFunction.Init:
			return JobMethod.InitializeAsync;
		case JobFunction.GetItems:
			return JobMethod.GetItemsAsync;
		case JobFunction.GetItemId:
			return JobMethod.GetItemIdAsync;
		case JobFunction.Process:
			return JobMethod.ProcessAsync;
		case JobFunction.Finalize:
			return JobMethod.FinalizeAsync;
		default:
			throw new Error("Unknown job function");
	}
}

function getApiDisposition(disposition: Disposition): ApiDisposition {
	switch (disposition) {
		case Disposition.NotComplete:
			return ApiDisposition.NotComplete;
		case Disposition.Successful:
			return ApiDisposition.Successful;
		case Disposition.Failed:
			return ApiDisposition.Failed;
		case Disposition.Cancelled:
			return ApiDisposition.Cancelled;
		default:
			throw new Error("Unknown disposition");
	}
}

export default class JobChannel {
	con: signalR.HubConnection;
	execution: Execution;

	constructor(connection: signalR.HubConnection, execution: Execution) {
		this.con = connection;
		this.execution = execution;
		console.log(`new JobChannel: ${this.con.connectionId}`);

		this.con.onclose(error =>
			console.log(`Hub connection closed: ${error?.message}`)
		);
		this.con.onreconnecting(error =>
			console.log(`Hub reconnecting: ${error?.message}`)
		);
		this.con.onreconnected(() => console.log(`Hub reconnected`));
		this.con.on("Initialized", () => {
			console.log("Channel initialized");
		});
		this.con.on("CancellationRequested", () => {
			execution.cancel();
		});
		execution.on(Events.ItemsCounted, e => {
			void this.callApiMethod("SetTotal", e.count);
		});
		execution.on(Events.StateChanged, _ => {
			void this.callApiMethod(
				"UpdateState",
				getInstanceState(execution),
				Object.values(execution.categories) ?? []
			);
		});
		execution.on(Events.FunctionCompleted, e => {
			void this.callApiMethod("MethodResult", {
				method: getJobMethod(e.result.name),
				error:
					e.result.error === undefined
						? undefined
						: {
								message: e.result.error.message,
								trace: e.result.error.stack
						  },
				duration: timeSpanFromMilliseconds(e.result.duration)
			});
		});
		execution.on(Events.ItemCompleted, e => {
			if (
				!e.result.isSuccessful ||
				execution.cfg.results.level === ResultsLevel.Successful
			) {
				void this.callApiMethod("ItemResult", {
					id: e.result.id,
					isSuccessful: e.result.isSuccessful,
					category: e.result.category,
					methods: {
						processAsync: {
							method: getJobMethod(JobFunction.Process),
							error:
								e.result.error === undefined
									? undefined
									: {
											message: e.result.error.message,
											trace: e.result.error.stack
									  },
							duration: timeSpanZero
						}
					}
				});
			}
		});
		execution.on(Events.Completed, e => {
			void this.callApiMethod(
				"MarkComplete",
				getApiDisposition(e.disposition),
				{},
				Object.values(execution.categories)
			);
		});
	}

	async callApiMethod(methodName: string, ...args: unknown[]) {
		try {
			console.log(
				`Calling ${methodName} ${args.toString()} (Channel ${this.con.state})`
			);
			await this.con.invoke(methodName, ...args);
		} catch (err) {
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			console.log(`error ${err}`);
			if (err instanceof Error)
				console.log(`Error calling ${methodName}: ${err.message}`);
		}
		console.log("Call complete");
	}
}

export async function connectToRunlyApi(execution: Execution) {
	const url = urlcat(
		execution.cfg.runlyApi.uri,
		`/nodes/results/${execution.cfg.runlyApi.instanceId}`
	);
	const con = new signalR.HubConnectionBuilder()
		.withUrl(url, {
			accessTokenFactory: () => execution.cfg.runlyApi.token
		})
		.withAutomaticReconnect()
		.configureLogging(signalR.LogLevel.Trace)
		// TODO: .net client calls .AddMessagePackProtocol()
		.build();

	console.log(`Connect: ${con.connectionId}`);
	const channel = new JobChannel(con, execution);

	await con.start();

	return channel;
}
